import type { Room } from '@obraplan/core/domain/takeoff/room';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import { stairSteps, type Stair } from '@obraplan/core/domain/takeoff/stair';
import { paraTexto } from './formato';
import { proximoNomeDaPeca, retanguloPorDoisCantos } from './fundacao-da-prancha';

/**
 * As contas da escada na prancha, sem tela (Fase 27, etapa 5 — D10, F17, RN-373).
 *
 * Moram à parte do desenho para que o que a prancha diz — o próximo nome, a seta da subida, as
 * linhas dos degraus, o retângulo do ambiente ESCADA — seja texto e número que se testa sem
 * montar a prancha.
 */

/** A cor da escada, na prancha e na legenda: âmbar, que se distingue do azul da laje. */
export const COR_DA_ESCADA = '#b45309';

/** O que a escada pede quando nasce, em centímetros: o espelho e o piso comuns, e 12 de laje. */
export const MEDIDAS_PADRAO_DA_ESCADA_EM_CM = {
  espelho: 17.5,
  piso: 28,
  espessura: 12,
} as const;

/** Quantos degraus o lance tem quando o contorno ainda não sugeriu nenhum. */
export const DEGRAUS_PADRAO = 10;

/** "E1", "E2"… — o próximo nome livre, na sequência da última escada desenhada. */
export const proximoNomeDeEscada = (escadas: readonly { readonly name: string }[]): string =>
  proximoNomeDaPeca(escadas, 'E');

/** Centímetros em metros, sem o ruído de ponto flutuante: 17,5 vira 0,175 e não 0,17500000000000002. */
export const emMetros = (centimetros: number): number => Math.round(centimetros * 10) / 1000;

/** "17,5 cm" — a medida como a obra fala. */
export const emTextoDeCentimetros = (centimetros: number): string =>
  `${paraTexto(Math.round(centimetros * 10) / 10)} cm`;

/** O rótulo da escada na prancha: o nome e os degraus — "E1 · 18 degraus". */
export function rotuloDaEscada(escada: Pick<Stair, 'name' | 'flights'>): string {
  const degraus = stairSteps(escada);
  return `${escada.name} · ${String(degraus)} ${degraus === 1 ? 'degrau' : 'degraus'}`;
}

// ── O desenho do lance ──────────────────────────────────────────────────────────

interface Caixa {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

export function caixaDoContorno(contorno: readonly Point[]): Caixa {
  const xs = contorno.map((ponto) => ponto.x);
  const ys = contorno.map((ponto) => ponto.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/** Os quatro cantos do retângulo envolvente — o ponto de partida do lance sobre o ambiente. */
export function retanguloEnvolvente(contorno: readonly Point[]): readonly Point[] {
  const { minX, maxX, minY, maxY } = caixaDoContorno(contorno);
  return retanguloPorDoisCantos({ x: minX, y: minY }, { x: maxX, y: maxY });
}

/**
 * O sentido da subida: do primeiro canto ao segundo — o primeiro e o terceiro vértice do
 * retângulo por dois cantos —, projetado no eixo mais comprido, que é o dos degraus.
 */
export interface SetaDoLance {
  readonly de: Point;
  readonly ate: Point;
  /** `true` quando a subida corre no eixo x; senão, no y. */
  readonly horizontal: boolean;
}

export function setaDoLance(contorno: readonly Point[]): SetaDoLance | undefined {
  const primeiro = contorno[0];
  const oposto = contorno[2] ?? contorno[contorno.length - 1];
  if (primeiro === undefined || oposto === undefined) return undefined;
  const { minX, maxX, minY, maxY } = caixaDoContorno(contorno);
  const horizontal = maxX - minX >= maxY - minY;
  const meioY = (minY + maxY) / 2;
  const meioX = (minX + maxX) / 2;
  if (horizontal) {
    const sobe = oposto.x >= primeiro.x;
    return {
      horizontal,
      de: { x: sobe ? minX : maxX, y: meioY },
      ate: { x: sobe ? maxX : minX, y: meioY },
    };
  }
  const sobe = oposto.y >= primeiro.y;
  return {
    horizontal,
    de: { x: meioX, y: sobe ? minY : maxY },
    ate: { x: meioX, y: sobe ? maxY : minY },
  };
}

/**
 * As linhas dos degraus: `degraus - 1` linhas, a intervalos iguais ao longo da subida,
 * cada uma de uma borda a outra do lance. Cada linha é um par de pontos.
 */
export function linhasDosDegraus(
  contorno: readonly Point[],
  degraus: number,
): readonly (readonly [Point, Point])[] {
  const seta = setaDoLance(contorno);
  if (seta === undefined || !Number.isInteger(degraus) || degraus < 2) return [];
  const { minX, maxX, minY, maxY } = caixaDoContorno(contorno);
  const linhas: (readonly [Point, Point])[] = [];
  for (let indice = 1; indice < degraus; indice += 1) {
    const fracao = indice / degraus;
    if (seta.horizontal) {
      const x = seta.de.x + (seta.ate.x - seta.de.x) * fracao;
      linhas.push([
        { x, y: minY },
        { x, y: maxY },
      ]);
    } else {
      const y = seta.de.y + (seta.ate.y - seta.de.y) * fracao;
      linhas.push([
        { x: minX, y },
        { x: maxX, y },
      ]);
    }
  }
  return linhas;
}

/**
 * Quantos degraus cabem no comprimento do lance: o lado mais comprido do retângulo dividido
 * pelo piso. É só a sugestão — a pessoa digita o número que o projeto manda.
 */
export function degrausSugeridos(contorno: readonly Point[], pisoEmMetros: number): number {
  if (!(pisoEmMetros > 0)) return DEGRAUS_PADRAO;
  const { minX, maxX, minY, maxY } = caixaDoContorno(contorno);
  const comprimento = Math.max(maxX - minX, maxY - minY);
  return Math.max(1, Math.round(comprimento / pisoEmMetros));
}

// ── O ambiente ESCADA (F17) ─────────────────────────────────────────────────────

/**
 * O contorno do ambiente ESCADA da área comum, quando há um traçado: o ponto de partida do
 * primeiro lance. A escada de concreto e o ambiente ESCADA são duas coisas — o ambiente mede
 * o acabamento, a escada mede o concreto —, mas o contorno de um quase sempre é o do outro.
 */
export function contornoDoAmbienteEscada(
  ambientes: readonly Pick<Room, 'kind' | 'outline'>[],
): readonly Point[] | undefined {
  const ambiente = ambientes.find(
    (candidato) =>
      candidato.kind === 'ESCADA' &&
      candidato.outline !== undefined &&
      candidato.outline.length >= 3,
  );
  return ambiente?.outline === undefined ? undefined : retanguloEnvolvente(ambiente.outline);
}
